<template>
  <div class="room-management">
    <div class="page-header">
      <h2><span class="breadcrumb-level">服务管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">宠境酒店</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">房型管理</span></h2>
      <div class="header-actions">
        <el-input v-model="searchKeyword" placeholder="搜索房型名称..." clearable prefix-icon="Search" style="width:240px" @input="onSearchInput" />
        <el-button type="primary" @click="handleAdd">新增房型</el-button>
      </div>
    </div>

    <el-table :data="rooms" stripe v-loading="loading">
      <el-table-column label="封面" width="70">
        <template #default="{ row }">
          <el-image :src="row.cover_image" style="width:45px;height:45px;border-radius:4px" fit="cover" />
        </template>
      </el-table-column>
      <el-table-column label="房型名称" prop="title" min-width="130" />
      <el-table-column label="普通价" width="100">
        <template #default="{ row }">¥{{ row.price || 0 }}</template>
      </el-table-column>
      <el-table-column label="库存" width="60">
        <template #default="{ row }">{{ row.stock }}</template>
      </el-table-column>
      <el-table-column label="状态" width="70">
        <template #default="{ row }">
          <el-switch v-model="row.status" @change="handleFieldChange(row, 'status')" />
        </template>
      </el-table-column>
      <el-table-column label="排序" width="120">
        <template #default="{ row }">
          <el-input-number v-model="row.sort_order" :min="0" size="small" @change="handleFieldChange(row, 'sort_order')" />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button size="small" @click="handleSpecialPrice(row)">特殊价</el-button>
          <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="pagination.total > 0"
      v-model:current-page="pagination.current_page"
      v-model:page-size="pagination.per_page"
      :total="pagination.total"
      layout="total, sizes, prev, pager, next, jumper"
      :page-sizes="[10,20,50]"
      small
      background
      style="margin-top:16px;justify-content:flex-end"
      @current-change="fetchData"
      @size-change="fetchData"
    />

    <!-- 房型编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑房型' : '新增房型'" width="800px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="房型名称" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="封面图">
          <Upload v-model="form.cover_image" :max-count="1" tip="推荐尺寸 宽 690 × 高 470 px" />
        </el-form-item>
        <el-form-item label="详情轮播图">
          <Upload v-model="form.images" multiple :max-count="10" tip="推荐尺寸 宽 690 × 高 470 px" />
        </el-form-item>
        <el-form-item label="简介">
          <el-input v-model="form.intro" type="textarea" :rows="2" maxlength="500" />
        </el-form-item>
        <el-form-item label="详情">
          <RichTextEditor v-model="form.content" />
        </el-form-item>
        <el-form-item label="普通价格">
          <el-input-number v-model="form.price" :precision="2" :min="0" style="width:200px" />
          <span style="margin-left:8px;color:#909399;font-size:12px;">元/只/自然日</span>
        </el-form-item>
        <el-form-item label="库存">
          <el-input-number v-model="form.stock" :min="0" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" />
        </el-form-item>
        <el-form-item label="服务人员">
          <el-select v-model="form.team_member_id" placeholder="选择绑定的服务人员" clearable style="width:100%">
            <el-option v-for="m in teamMemberOptions" :key="m.id" :label="m.name" :value="m.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>

    <!-- 特殊日期价格弹窗 -->
    <el-dialog v-model="priceDialogVisible" title="特殊日期价格" width="820px">
      <div class="price-desc" style="margin-bottom:12px;color:#909399;font-size:13px;">
        设置特殊日期范围内的价格，优先级高于普通价格。日期重叠时以最新的为准。
      </div>
      <el-table :data="specialPrices" border style="width:100%">
        <el-table-column label="开始日期" min-width="180">
          <template #default="{ row }">
            <el-date-picker v-model="row.start_date" type="date" value-format="YYYY-MM-DD" size="small" style="width:100%" />
          </template>
        </el-table-column>
        <el-table-column label="结束日期" min-width="180">
          <template #default="{ row }">
            <el-date-picker v-model="row.end_date" type="date" value-format="YYYY-MM-DD" size="small" style="width:100%" />
          </template>
        </el-table-column>
        <el-table-column label="价格" min-width="140">
          <template #default="{ row }">
            <el-input-number v-model="row.price" :precision="2" :min="0" size="small" style="width:100%" />
          </template>
        </el-table-column>
        <el-table-column label="库存" min-width="120">
          <template #default="{ row }">
            <el-input-number v-model="row.stock" :min="0" size="small" style="width:100%" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template #default="{ row, $index }">
            <el-button size="small" text type="danger" @click="specialPrices.splice($index, 1)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div style="margin-top:12px;display:flex;gap:8px;">
        <el-button @click="addSpecialPriceRow">+ 新增特殊价格</el-button>
        <el-button type="primary" @click="handleSpecialPriceSave">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import RichTextEditor from '../../components/RichTextEditor.vue';
import * as hotelApi from '../../api/hotel';
import * as teamApi from '../../api/team';
import Upload from '../../components/Upload.vue';

const teamMemberOptions = ref([]);

const rooms = ref([]);
const loading = ref(false);
const pagination = reactive({ current_page: 1, per_page: 20, total: 0, last_page: 1 });
const searchKeyword = ref('');
const dialogVisible = ref(false);
const priceDialogVisible = ref(false);
const isEdit = ref(false);
const currentRoomId = ref(null);
const formRef = ref(null);

const specialPrices = ref([]);

const form = ref({
  title: '', cover_image: '', images: [], intro: '', content: '',
  price: 0, stock: 0, sort_order: 0, status: true, team_member_id: null,
});
const rules = { title: [{ required: true, message: '请输入房型名称', trigger: 'blur' }] };

let searchTimer = null;
const onSearchInput = () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => fetchData(), 300);
};

const fetchData = async () => {
  loading.value = true;
  try { const params = { page: pagination.current_page, per_page: pagination.per_page, search: searchKeyword.value || undefined }; const res = await hotelApi.getRoomTypes(params); rooms.value = res.data || []; pagination.current_page = res.pagination?.current_page || 1; pagination.total = res.pagination?.total || 0; }
  catch { ElMessage.error('获取房型列表失败'); } finally { loading.value = false; }
};

const handleAdd = () => {
  isEdit.value = false;
  form.value = { title: '', cover_image: '', images: [], intro: '', content: '', price: 0, stock: 0, sort_order: 0, status: true, team_member_id: null };
  dialogVisible.value = true;
};

const handleEdit = (row) => {
  isEdit.value = true;
  form.value = { ...row, images: row.images || [] };
  dialogVisible.value = true;
};

const handleFieldChange = (row, field) => {
  hotelApi.updateRoomType(row.id, { [field]: row[field] });
};

const handleSave = async () => {
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) return;
  try {
    if (isEdit.value) { await hotelApi.updateRoomType(form.value.id, form.value); ElMessage.success('更新成功'); }
    else { await hotelApi.createRoomType(form.value); ElMessage.success('创建成功'); }
    dialogVisible.value = false; await fetchData();
  } catch { ElMessage.error('操作失败'); }
};

const handleDelete = (row) => {
  ElMessageBox.confirm('确定删除？', '提示').then(async () => {
    await hotelApi.deleteRoomType(row.id); ElMessage.success('删除成功'); await fetchData();
  }).catch(() => {});
};

// ===== 特殊日期价格 =====
const handleSpecialPrice = async (row) => {
  currentRoomId.value = row.id;
  try {
    const res = await hotelApi.getSpecialPrices(row.id);
    specialPrices.value = (res.data || []).map(p => ({
      id: p.id,
      start_date: p.start_date,
      end_date: p.end_date,
      price: p.price,
      stock: p.stock,
    }));
  } catch {
    specialPrices.value = [];
  }
  priceDialogVisible.value = true;
};

const addSpecialPriceRow = () => {
  specialPrices.value.push({
    id: null,
    start_date: '',
    end_date: '',
    price: 0,
    stock: 0,
  });
};

const handleSpecialPriceSave = async () => {
  // 逐条保存（新增/更新）
  for (const item of specialPrices.value) {
    if (!item.start_date || !item.end_date) {
      ElMessage.warning('请填写完整的日期范围');
      return;
    }
    try {
      await hotelApi.upsertSpecialPrice(currentRoomId.value, item);
    } catch {
      ElMessage.error('保存失败');
      return;
    }
  }
  ElMessage.success('特殊价格已保存');
  priceDialogVisible.value = false;
};

const fetchTeamMembers = async () => {
  try { const res = await teamApi.getTeamMembers(); teamMemberOptions.value = res.data || []; }
  catch { teamMemberOptions.value = []; }
};

onMounted(() => { fetchData(); fetchTeamMembers(); });

</script>

<style scoped>
.upload-img { width: 200px; height: 120px; object-fit: cover; border-radius: 4px; }
.upload-icon { font-size: 28px; color: #8c939d; }
.avatar-uploader { border: 1px dashed #d9d9d9; border-radius: 6px; cursor: pointer; padding: 20px; text-align: center; display: inline-block; }
.avatar-uploader:hover { border-color: #409eff; }
</style>
